<script>
  import mime from "mime";
  import { onMount, getContext, tick } from "svelte";
  import { copyToClipboard } from "overline";
  import { tooltip } from "svelte-utilities";
  import { navigate } from "svelte-routing";
  import upperFirst from "lodash/upperFirst";

  import XICon from "@local/assets/icons/x.svg";
  import SpinnerIcon from "@local/assets/icons/spinner.svg";
  import QuickSelectIcon from "@local/assets/icons/quick-select.svg";
  import CopyIcon from "@local/assets/icons/duplicate.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";

  import {
    currentTool,
    clipboard,
    settingTriopsUrl as triopsUrl,
    tableImportLlmModel,
    tableImportReasoningLevel,
  } from "#src/stores/ui.js";
  import { importTable } from "#src/api/ai.js";
  import { alerts } from "#src/stores/alerts.js";
  import nameMinusExtension from "@local/extensions/utilities/name-minus-extension.js";
  import extension from "@local/extensions/utilities/extension.js";
  import imgClipToBase64 from "#src/extensions/dom/img-clip-to-base64.js";
  import dataUrlToBase64 from "#src/extensions/data-url-to-base64.js";
  import { api } from "#src/api";

  import eb from "#src/extensions/event-bus.js";

  export let aiClipRegion;
  export let doc;

  const org = getContext("org");

  const recordName = {
    item: "items",
    collection: "openings",
    type: "type",
    document_note: "note",
  };

  let visible = false;
  let working = false;
  let finishing = false;
  let errorMessage = null;
  let data = null;
  let importAs = "item";
  let showImportAs = false;

  function toggleVisible() {
    visible = !visible;
    errorMessage = null;
    finishing = false;

    if (!visible) {
      $currentTool = "select";
      aiClipRegion = null;
    }
  }

  function pickRegion() {
    aiClipRegion = null;
    errorMessage = null;
  }

  async function clipImage(clip) {
    const page = doc.data.pages[clip.page_id];
    if (!page) return null;

    const ext = extension(page.name);

    if (ext === "pdf") {
      const path = encodeURIComponent(`${nameMinusExtension(page.name)}.${page.page}.tiff`);
      const { x, y, width: w, height: h, drawing_scale, drawing_offset } = clip;
      const cpath = `${x},${y},${w},${h}`;
      const url = `${$triopsUrl}/iiif/2/drawings/${path}/${cpath}/full/0/default.jpg`;
      const img = await fetch(url);
      const buf = await img.arrayBuffer();
      const base64 = btoa(new Uint8Array(buf).reduce((data, byte) => data + String.fromCharCode(byte), ""));
      return {
        type: "base64",
        media_type: "image/jpeg",
        drawing_scale,
        drawing_offset,
        url,
        data: base64,
      };
    } else {
      const res = await api.storage.from("documents").createSignedUrl(page.name, 60);
      if (res.error) return null;
      const url = res.data.signedUrl;
      // See data-url-to-base64.js.
      const { data, mediaType } = dataUrlToBase64(await imgClipToBase64(url, clip));
      return {
        type: "base64",
        media_type: mediaType || mime.getType(ext) || "image/jpeg",
        data,
        drawing_scale: clip.drawing_scale,
        drawing_offset: clip.drawing_offset,
        url,
      };
    }
  }

  async function importTableFromRegion() {
    working = true;
    errorMessage = null;
    const image = await clipImage(aiClipRegion.clip);
    const results = await importTable({
      image,
      clip: aiClipRegion.clip,
      model: $tableImportLlmModel,
      reasoning_level: $tableImportReasoningLevel,
      organization_id: $org.id,
    });

    working = false;

    if (results.error) {
      console.log(results.error);
      errorMessage = results.error;
      finishing = false;
    } else if (!results.data?.data?.rows?.length) {
      console.log(results);
      errorMessage = "Unable to extract table data from the selected region.";
      finishing = false;
    } else {
      finishing = true;
      data = results.data;
    }

    working = false;
  }

  function copyTableToClipboard() {
    if (!data?.data) {
      alerts.add({ type: "error", message: "No table data to copy" });
      return;
    }
    const str = [data.data.columns.join("\t"), ...data.data.rows.map((row) => row.join("\t"))].join("\n");
    copyToClipboard(str);
    alerts.add({ type: "ok", message: "Copied table data to clipboard" });
  }

  function setImportAs(type) {
    importAs = type;
    showImportAs = false;
  }

  async function importFromTable() {
    if (!data?.data) {
      alerts.add({ type: "error", message: "No table data to import" });
      return;
    }
    $clipboard = {
      type: "table-data",
      data: [data.data.columns, ...data.data.rows],
    };

    if (importAs === "item") {
      await navigate("../items");
      await tick();
      await tick();
      eb.dispatch("import-csv", "spreadsheet");
    } else if (importAs === "collection") {
      await navigate("../openings");
      await tick();
      await tick();
      eb.dispatch("import-csv", "spreadsheet");
    }
  }

  onMount(() => {
    eb.on("ai-clip", toggleVisible);

    return () => {
      eb.unsubscribe("ai-clip", toggleVisible);
    };
  });
</script>

{#if visible}
  <div class="w-full flex justify-end p-4 absolute z-30 pointer-events-none text-xs">
    <div class="w-72 pointer-events-auto">
      <div class="bg-white rounded border shadow-lg w-full">
        <div class="p-2 flex items-center border-b">
          <div class="grow">AI Import</div>
          <button on:click={toggleVisible} class="rounded p-1 hover:bg-gray-200">
            <XICon />
          </button>
        </div>
        <div class="p-2">
          {#if !aiClipRegion}
            <div class="text-xs italic text-gray-500">Drag to select a region within a page.</div>
          {:else if errorMessage}
            <div class="flex flex-col items-center gap-2">
              <div class="text-xs text-red-500">
                {errorMessage}
              </div>
            </div>
            <div class="flex justify-between gap-2">
              <button class="btn-text" on:click={importTableFromRegion}> Import again </button>
              <button class="btn-text" on:click={pickRegion}> Select New Region </button>
            </div>
          {:else if working}
            <div class="flex gap-2">
              <div class="relative">
                <div class="animate-spin w-4 h-4">
                  <SpinnerIcon />
                </div>
              </div>
              <div class="text-xs italic text-gray-500">Processing...</div>
            </div>
          {:else if finishing}
            <div class="space-y-4">
              <div class="text-xs">Table imported successfully!</div>
              <div class="flex justify-between gap-2">
                <button class="btn-text" on:click={importTableFromRegion}> Import again </button>
                <div class="flex gap-1">
                  <button class="btn-text" on:click={importFromTable}
                    >Import as {upperFirst(recordName[importAs])}</button>
                  <button
                    on:click={() => (showImportAs = !showImportAs)}
                    class="rounded p-1 hover:bg-gray-200 relative">
                    <CaretDownIcon />
                    {#if showImportAs}
                      <div class="dropdown-container text-right right-0">
                        <button
                          class="dropdown-button-item"
                          on:click|stopPropagation={() => setImportAs("item")}>Import as Items</button>
                        <button
                          class="dropdown-button-item"
                          on:click|stopPropagation={() => setImportAs("collection")}
                          >Import as Openings</button>
                      </div>
                    {/if}
                  </button>
                </div>
                <button
                  class="rounded p-1 hover:bg-gray-200"
                  on:click={copyTableToClipboard}
                  use:tooltip={{ text: "Copy data to clipboard" }}>
                  <CopyIcon />
                </button>
              </div>
            </div>
          {:else}
            <div class="flex flex-col items-center gap-2">
              <button class="btn-text" on:click={importTableFromRegion}> Import Table </button>
              <!-- <button class="btn-text" on:click={importFromRegion}> Import Items From Region </button> -->
            </div>
            <div class="flex justify-end">
              <button
                class="p-1 rounded hover:bg-gray-200"
                on:click={pickRegion}
                use:tooltip={{ text: "Select a new region" }}>
                <QuickSelectIcon /></button>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}
